import React, { useMemo } from 'react';
import { PhaseDefs } from '../../constants/definitions';

const ComparisonCompetencyTableWeb = ({ session1Competencies = [], session2Competencies = [], phase }) => {
    const PHASES = ['Empathize', 'Define', 'Ideate', 'Prototype', 'Test', 'Communicate'];

    // Calculate competency data for each phase
    const phaseData = useMemo(() => {
        const data = {};
        
        PHASES.forEach(phase => {
            data[phase] = {
                session1Unique: [],
                session2Unique: [],
                shared: []
            };
        });

        // Get competency IDs for each session
        const session1Ids = new Set(session1Competencies.map(comp => comp.id));
        const session2Ids = new Set(session2Competencies.map(comp => comp.id));

        // Process session 1 competencies
        session1Competencies.forEach(competency => {
            const phase = competency.abilities?.phase || 'Unknown';
            if (PHASES.includes(phase)) {
                if (session2Ids.has(competency.id)) {
                    // Shared competency
                    data[phase].shared.push(competency);
                } else {
                    // Unique to session 1
                    data[phase].session1Unique.push(competency);
                }
            }
        });

        // Process session 2 competencies
        session2Competencies.forEach(competency => {
            const phase = competency.abilities?.phase || 'Unknown';
            if (PHASES.includes(phase)) {
                if (!session1Ids.has(competency.id)) {
                    // Unique to session 2
                    data[phase].session2Unique.push(competency);
                }
            }
        });

        return data;
    }, [session1Competencies, session2Competencies]);

    const styles = {
        container: {
            marginBottom: '30px',
        },
        phaseHeader: {
            fontSize: '14px',
            fontWeight: '600',
            marginBottom: '10px',
            color: '#333333',
            fontFamily: 'Poppins, sans-serif',
        },
        phaseDescription: {
            fontSize: '12px',
            marginBottom: '18px',
            lineHeight: '1.4',
            color: '#666666',
            fontFamily: 'Poppins, sans-serif',
        },
        table: {
            width: '100%',
        },
        tableHeader: {
            display: 'flex',
            flexDirection: 'row',
            backgroundColor: '#f5f5f5',
            borderBottom: '1px solid #e0e0e0',
        },
        tableRow: {
            display: 'flex',
            flexDirection: 'row',
            borderBottom: '1px solid #f0f0f0',
            minHeight: '30px',
        },
        // Base styles
        baseText: {
            fontFamily: 'Poppins, sans-serif',
        },
        headerCell: {
            fontSize: '12px',
            fontWeight: '600',
            color: '#444444',
            padding: '8px',
            fontFamily: 'Poppins, sans-serif',
        },
        cell: {
            fontSize: '10px',
            color: '#333333',
            fontFamily: 'Poppins, sans-serif',
        },
        columnContent: {
            padding: '6px 4px',
        },
        competencyItem: {
            marginBottom: '4px',
            padding: '2px 6px 4px',
            borderBottom: '0.5px dashed #e0e0e0',
        },
        firstCompetencyItem: {
            paddingTop: '8px',
        },
        lastCompetencyItem: {
            marginBottom: '0',
            paddingBottom: '0',
            borderBottom: 'none',
        },
        // Column styles with colors
        session1Column: {
            width: '33.33%',
            backgroundColor: '#F8F9FA',
        },
        session2Column: {
            width: '33.33%',
            backgroundColor: '#F8F9FA',
        },
        sharedColumn: {
            width: '33.33%',
            backgroundColor: '#E8F5E9',
        },
        // Header cell variants
        headerCellSession1: {
            backgroundColor: '#F8F9FA',
        },
        headerCellSession2: {
            backgroundColor: '#F8F9FA',
        },
        headerCellShared: {
            backgroundColor: '#E8F5E9',
        },
        emptyCell: {
            textAlign: 'center',
            color: '#999999',
            fontStyle: 'italic',
            fontSize: '10px',
            padding: '8px',
        }
    };


    const getMaxRows = (phase) => {
        const session1Count = phaseData[phase].session1Unique.length;
        const session2Count = phaseData[phase].session2Unique.length;
        const sharedCount = phaseData[phase].shared.length;
        return Math.max(session1Count, session2Count, sharedCount, 1);
    };

    const getCategoryIndicator = (category) => {
        switch (category) {
            case 'skills':
                return '(S)';
            case 'knowledge':
                return '(K)';
            case 'mindset':
                return '(M)';
            case 'tool':
                return '(I)';
            default:
                return '';
        }
    };

    if (!phase || !PHASES.includes(phase)) {
        return <div>Invalid phase specified</div>;
    }

    const maxRows = getMaxRows(phase);
    const session1Unique = phaseData[phase].session1Unique;
    const session2Unique = phaseData[phase].session2Unique;
    const shared = phaseData[phase].shared;

    return (
        <div style={styles.container}>
            <div style={styles.phaseHeader}>{phase}</div>
            <div style={styles.phaseDescription}>{PhaseDefs[phase.toUpperCase()]}</div>
            
            <div style={styles.table}>
                <div style={styles.tableHeader}>
                    <div style={{...styles.headerCell, ...styles.headerCellSession1, ...styles.session1Column}}>
                        Session 1 Unique ({session1Unique.length})
                    </div>
                    <div style={{...styles.headerCell, ...styles.headerCellSession2, ...styles.session2Column}}>
                        Session 2 Unique ({session2Unique.length})
                    </div>
                    <div style={{...styles.headerCell, ...styles.headerCellShared, ...styles.sharedColumn}}>
                        Shared ({shared.length})
                    </div>
                </div>

                {Array.from({ length: maxRows }, (_, rowIndex) => (
                    <div key={rowIndex} style={styles.tableRow}>
                        <div style={styles.session1Column}>
                            {rowIndex < session1Unique.length ? (
                                <div style={{...styles.cell, ...styles.columnContent}}>
                                    <div style={{
                                        ...styles.competencyItem,
                                        ...(rowIndex === 0 ? styles.firstCompetencyItem : {}),
                                        ...(rowIndex === session1Unique.length - 1 ? styles.lastCompetencyItem : {})
                                    }}>
                                        {session1Unique[rowIndex].name} {getCategoryIndicator(session1Unique[rowIndex].category)}
                                    </div>
                                </div>
                            ) : (
                                <div style={{...styles.cell, ...styles.columnContent}}>
                                    <div style={styles.emptyCell}></div>
                                </div>
                            )}
                        </div>
                        
                        <div style={styles.session2Column}>
                            {rowIndex < session2Unique.length ? (
                                <div style={{...styles.cell, ...styles.columnContent}}>
                                    <div style={{
                                        ...styles.competencyItem,
                                        ...(rowIndex === 0 ? styles.firstCompetencyItem : {}),
                                        ...(rowIndex === session2Unique.length - 1 ? styles.lastCompetencyItem : {})
                                    }}>
                                        {session2Unique[rowIndex].name} {getCategoryIndicator(session2Unique[rowIndex].category)}
                                    </div>
                                </div>
                            ) : (
                                <div style={{...styles.cell, ...styles.columnContent}}>
                                    <div style={styles.emptyCell}></div>
                                </div>
                            )}
                        </div>

                        <div style={styles.sharedColumn}>
                            {rowIndex < shared.length ? (
                                <div style={{...styles.cell, ...styles.columnContent}}>
                                    <div style={{
                                        ...styles.competencyItem,
                                        ...(rowIndex === 0 ? styles.firstCompetencyItem : {}),
                                        ...(rowIndex === shared.length - 1 ? styles.lastCompetencyItem : {})
                                    }}>
                                        {shared[rowIndex].name} {getCategoryIndicator(shared[rowIndex].category)}
                                    </div>
                                </div>
                            ) : (
                                <div style={{...styles.cell, ...styles.columnContent}}>
                                    <div style={styles.emptyCell}></div>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ComparisonCompetencyTableWeb;
